/**
 * Aperture/Kinfolk gallery-print layered frame — two photo-sized colored
 * rectangles offset in opposite directions behind the child, creating a
 * four-corner peek that reads as receding depth (front child, middle layer,
 * back layer, page background). Same visual grammar as the hero photo.
 *
 * Colors are locked to the palette pair the user committed to: yellow
 * (up-right, primary) + cobalt (down-left, secondary). See PRD 2026-02-22x.
 *
 * The wrapper is `relative` so any parent with its own positioning still
 * governs layout. Accents are `lg:block hidden` — hidden on mobile to keep
 * small screens clean.
 *
 * Props:
 *   offset: xs (6px/8px) | sm (8px/10px) | md (12px/14px) | lg (16px/20px)
 *   className: pass-through wrapper class (e.g., "absolute inset-0")
 *   children: the framed content (photo, card, etc.)
 */
const OFFSETS = {
  xs: ["translate-x-1.5 -translate-y-1.5", "-translate-x-2 translate-y-2"],
  sm: ["translate-x-2 -translate-y-2",     "-translate-x-2.5 translate-y-2.5"],
  md: ["translate-x-3 -translate-y-3",     "-translate-x-3.5 translate-y-3.5"],
  lg: ["translate-x-4 -translate-y-4",     "-translate-x-5 translate-y-5"],
};

export const StackedFrame = ({ children, offset = "sm", className }) => {
  const [primary, secondary] = OFFSETS[offset] || OFFSETS.sm;
  // Default to `relative` when no positioning class was passed. Callers that
  // supply their own positioning (e.g., "absolute inset-0") take precedence.
  const wrapper = className || "relative";
  return (
    <div className={wrapper} data-testid="stacked-frame">
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 z-0 hidden rounded-md bg-yellow lg:block ${primary}`}
      />
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 z-0 hidden rounded-md bg-cobalt/85 lg:block ${secondary}`}
      />
      <div className="relative z-10 h-full w-full">{children}</div>
    </div>
  );
};
